import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { useBilling } from "../context/BillingContext";
import Header from "../components/Header";
import BottomTabBar from "../components/BottomTabBar";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { refreshEntitlements, triggerUnlockCelebration } = useBilling();
  const [state, setState] = useState("checking"); // checking | granted | pending | failed
  const [sku, setSku] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await api.billingStatus(sessionId);
        if (r.entitlement_granted) {
          setSku(r.sku);
          setState("granted");
          await refreshEntitlements();
          triggerUnlockCelebration(r.sku);
          return;
        }
        if (r.payment_status === "paid") {
          // Shouldn't happen (entitlement_granted should flip too), but just in case
          setSku(r.sku);
          setState("granted");
          await refreshEntitlements();
          triggerUnlockCelebration(r.sku);
          return;
        }
      } catch { /* ignore, retry */ }
      if (attempts >= MAX_ATTEMPTS) {
        setState("pending");
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshEntitlements, triggerUnlockCelebration]);

  // Auto-redirect home shortly after the entitlement is granted so the user
  // never has to tap a button to "go back" — they land straight in the app.
  useEffect(() => {
    if (state !== "granted") return;
    const t = setTimeout(() => nav("/"), 1800);
    return () => clearTimeout(t);
  }, [state, nav]);

  return (
    <div className="warm-bg min-h-screen">
      <Header />
      <main className="max-w-xl mx-auto px-6 pt-16 pb-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-glow rounded-3xl p-8 text-center"
          data-testid="billing-success-card"
        >
          {state === "checking" && (
            <>
              <Loader2 className="w-10 h-10 text-[#FF4D8D] animate-spin mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white mb-2 tracking-tight">Confirming your unlock…</h1>
              <p className="text-sm text-[#C9C7D9]">Hold tight — this usually takes a few seconds.</p>
            </>
          )}
          {state === "granted" && (
            <>
              <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF4D8D] to-[#7B61FF] items-center justify-center mb-4 shadow-[0_0_40px_rgba(255,77,141,0.6)]">
                <Check className="w-7 h-7 text-white" strokeWidth={3} />
              </div>
              <h1 className="font-display text-3xl font-bold text-white mb-2">Unlocked successfully 🔥</h1>
              <p className="text-sm text-[#C9C7D9] mb-6">
                {sku && sku.startsWith("sub_")
                  ? "Premium is active — all packs, unlimited play, video responses, AI packs."
                  : "Pack unlocked and saved to your account. Now go play."}
              </p>
              <button
                onClick={() => nav("/")}
                className="btn-gradient rounded-full px-6 py-3 font-bold active:scale-95"
                data-testid="billing-success-back"
              >
                Start playing →
              </button>
            </>
          )}
          {state === "pending" && (
            <>
              <Loader2 className="w-10 h-10 text-[#00E5FF] animate-spin mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white mb-2 tracking-tight">Still processing…</h1>
              <p className="text-sm text-[#C9C7D9] mb-6">
                Payment looks good but unlock is taking a moment. It'll show up automatically — you can head back and keep playing.
              </p>
              <button
                onClick={() => nav("/")}
                className="btn-gradient rounded-full px-6 py-3 font-bold active:scale-95"
                data-testid="billing-success-back"
              >
                Back to VibeCheck
              </button>
            </>
          )}
          {state === "failed" && (
            <>
              <div className="inline-flex w-14 h-14 rounded-2xl bg-red-500/20 border border-red-500/40 items-center justify-center mb-4">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2 tracking-tight">Couldn't confirm the unlock</h1>
              <p className="text-sm text-[#C9C7D9] mb-6">No session id in the URL. If you paid, refresh in a moment.</p>
              <button
                onClick={() => nav("/")}
                className="btn-gradient rounded-full px-6 py-3 font-bold active:scale-95"
                data-testid="billing-success-back"
              >
                Back to VibeCheck
              </button>
            </>
          )}
        </motion.div>
      </main>
      <BottomTabBar />
    </div>
  );
}
